<div class="conduct-page container-fluid">
  <!-- Page Header -->
  <div class="flex items-center justify-between flex-wrap gap-4 mb-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-900 tracking-tight m-0">Conduct Time Logs</h1>
      <p class="text-sm text-gray-500 m-0 mt-1">
        Compare SuperSee tracked time with Conduct task logs.
      </p>
    </div>

    <!-- Header Actions: Sync Logs -->
    <div>
      <button type="button" (click)="fetchTimelogs(true)" [disabled]="loading"
        class="!h-9 flex items-center justify-center gap-1.5 px-3.5 !font-semibold !text-xs border !rounded-md !text-[#5459d1] !border-[#5459d1] bg-white hover:!bg-indigo-50/80 shadow-2xs transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        matTooltip="Sync latest timelogs from Conduct">
        <mat-spinner *ngIf="loading" diameter="16" class="shrink-0"></mat-spinner>
        <span *ngIf="!loading" class="material-symbols-outlined !text-[17px] leading-none">sync</span>
        <span class="text-xs font-semibold leading-none">Sync Logs</span>
      </button>
    </div>

    <!-- KPI Overview Metrics (5 Cards Responsive Layout) -->
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 w-full mb-6">
      <div *ngFor="let stat of conductStatsCards"
        class="conduct-stat-card bg-white rounded-xl px-5 py-5 hover:bg-gray-50/60 shadow-[0_4px_15px_#00000008] hover:shadow-md transition-all duration-300 ease-in-out cursor-pointer">
        <div class="flex items-start gap-3" *ngIf="!loading">
          <!-- Icon with Background -->
          <div [class]="'p-2.5 rounded-lg flex items-center justify-center ' + (stat.iconBgColor || 'bg-blue-100')">
            <span [class]="'material-symbols-outlined ' + (stat.iconColor || 'text-blue-500')"
              style="font-size: 24px;">{{
              stat.icon }}</span>
          </div>
          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p class="text-gray-500 text-sm font-normal m-0">{{ stat.label }}</p>
            <p class="text-gray-900 text-xl md:text-2xl font-bold m-0 mt-1 truncate" [matTooltip]="stat.value"
              matTooltipPosition="after">{{ stat.value }}</p>
            <p *ngIf="stat.subtitle" class="text-gray-500 text-xs mt-1 m-0 truncate" [matTooltip]="stat.subtitle">{{
              stat.subtitle }}</p>
            <!-- Progress Bar for Match Rate -->
            <div *ngIf="stat.progress !== undefined"
              class="w-full bg-gray-100 rounded-full h-1.5 mt-2.5 overflow-hidden">
              <div [class]="'h-1.5 rounded-full transition-all duration-500 ' + (stat.progressColor || 'bg-amber-500')"
                [style.width.%]="stat.progress"></div>
            </div>
          </div>
        </div>
        <div *ngIf="loading" class="flex items-start justify-center p-4 gap-2">
          <div class="rounded-lg flex items-center justify-center bg-white">
            <ngx-skeleton-loader class="skeleton-avatar"
              [theme]="{ 'border-radius': '8px', height: '50px', width: '50px' }"></ngx-skeleton-loader>
          </div>
          <!-- Content -->
          <div class="flex-1">
            <p class="text-gray-500 text-sm font-normal m-0 p-0" *ngFor="let i of [1,2,3]">
              <ngx-skeleton-loader class="skeleton-avatar"
                [theme]="{ 'border-radius': '4px', height: '14px', width: '100%' }"></ngx-skeleton-loader>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Card with Filters & Workbench Views -->
    <mat-card class="conduct-main-card fluid-ss mb-6">
      <mat-card-content class="p-5 sm:p-6">
        <!-- Filter Controls Bar -->
        <div class="flex items-center justify-between flex-wrap gap-3.5 mb-1 border-b border-gray-100 pb-5">
          <!-- Left: Filters row -->
          <div class="flex items-center gap-3 flex-wrap flex-1 min-w-[280px]">
            <!-- Date Filter: Stepper + Presets Dropdown matching SuperSee Theme -->
            <div class="conduct-date-filter-group" role="group" aria-label="Conduct log date range">
              <button type="button" mat-icon-button class="conduct-date-nav-btn" [disabled]="loading"
                (click)="onPreviousDate()" matTooltip="Previous day">
                <mat-icon>keyboard_arrow_left</mat-icon>
              </button>

              <div class="date-range-trigger-wrap relative">
                <button #dateRangeButton type="button" class="date-range-trigger" (click)="toggleDateDropdown()"
                  [class.active]="isOpenDateDropdown" [disabled]="loading" aria-haspopup="dialog"
                  [attr.aria-expanded]="isOpenDateDropdown">
                  <span class="material-symbols-outlined date-range-trigger-icon"
                    aria-hidden="true">calendar_month</span>
                  <span class="date-range-trigger-text">{{ selectedFilterLabel }}</span>
                  <span class="material-symbols-outlined date-range-trigger-caret" aria-hidden="true">expand_more</span>
                </button>

                <!-- Date Range & Presets Floating Dropdown -->
                <div *ngIf="isOpenDateDropdown" #dateRangeDropdown class="date-range-dropdown shadow-xl">
                  <mat-form-field appearance="outline" class="w-full conduct-daterange-picker-field"
                    subscriptSizing="dynamic">
                    <mat-date-range-input [rangePicker]="conductRangePicker" [formGroup]="selectedDateRange"
                      [min]="minDate" [max]="maxDate">
                      <input matStartDate placeholder="Start date" readonly formControlName="start">
                      <input matEndDate placeholder="End date" readonly formControlName="end">
                    </mat-date-range-input>
                    <mat-datepicker-toggle matIconSuffix [for]="conductRangePicker"></mat-datepicker-toggle>
                    <mat-date-range-picker #conductRangePicker
                      (closed)="onDateRangePickerClosed()"></mat-date-range-picker>
                  </mat-form-field>

                  <div class="preset-filters-block mt-3">
                    <div
                      class="preset-filters-title text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      Preset Filters</div>
                    <div class="preset-filters-list flex flex-col gap-1">
                      <button type="button" *ngFor="let f of presetFilters" (click)="setDatePreset(f)"
                        class="preset-filter-item text-left px-3 py-2 text-xs font-medium rounded-md hover:bg-indigo-50 hover:text-[#5459d1] transition-colors bg-transparent border-0 cursor-pointer"
                        [class.active-preset]="selectedPreset === f.value">
                        {{ f.label }}
                      </button>
                      <button type="button" (click)="conductRangePicker.open()"
                        class="preset-filter-item text-left px-3 py-2 text-xs font-medium rounded-md hover:bg-indigo-50 hover:text-[#5459d1] text-slate-600 transition-colors bg-transparent border-0 cursor-pointer">
                        Custom range…
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <button type="button" mat-icon-button class="conduct-date-nav-btn"
                [disabled]="loading || isNextDateDisabled()" (click)="onNextDate()" matTooltip="Next day">
                <mat-icon>keyboard_arrow_right</mat-icon>
              </button>
            </div>

            <!-- Search Box: Shared app-employee-search in Input Mode -->
            <div class="conduct-filter-search-wrap min-w-[200px] flex-1 max-w-[260px] h-[44px]">
              <app-employee-search [userList]="userList" [placeholder]="'Search employee, task...'"
                [isNeedUserReturn]="true" [isInputSearch]="true" [initialValue]="searchQuery"
                class="w-full h-full block" (searchEmployeeName)="onSearchChange($event)">
              </app-employee-search>
            </div>

            <!-- Employee Dropdown: Shared app-employee-search in Dropdown Mode -->
            <div class="conduct-filter-employee-wrap min-w-[190px] flex-1 max-w-[240px] h-[44px]">
              <app-employee-search [userList]="userList" [placeholder]="'All Employees'"
                [selectedEmployeeId]="selectedEmployeeId" class="w-full h-full block"
                (employeeSelected)="onEmployeeSelected($event)">
              </app-employee-search>
            </div>

            <!-- Project Filter Dropdown: Styled with SuperSee Theme -->
            <div class="conduct-filter-project-wrap min-w-[160px] max-w-[210px] h-[44px]">
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="conduct-mat-select w-full h-full">
                <mat-select [(value)]="selectedProjectId" (selectionChange)="onProjectFilterChange($event.value)"
                  panelClass="conduct-select-panel" placeholder="All Projects">
                  <mat-select-trigger>
                    <div class="flex items-center gap-2 truncate text-xs font-semibold text-slate-800">
                      <span
                        class="material-symbols-outlined text-[17px] text-[#5459d1] leading-none shrink-0">folder</span>
                      <span class="truncate">{{ getSelectedProjectLabel() }}</span>
                    </div>
                  </mat-select-trigger>
                  <mat-option *ngFor="let p of availableProjects" [value]="p.id">
                    <div class="flex items-center gap-2 text-xs">
                      <span class="material-symbols-outlined text-[16px] text-slate-400">folder</span>
                      <span>{{ p.name }}</span>
                    </div>
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <!-- Right: Reset Action & Segmented Toggle View (By Employee | By Project) -->
          <div class="flex items-center gap-4 shrink-0">
            <!-- Reset Button: Clean ghost button matching new design -->
            <button type="button" (click)="resetAllFilters()"
              class="conduct-reset-btn inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#5459d1] transition-colors bg-transparent border-0 cursor-pointer py-1 px-1.5 select-none"
              matTooltip="Reset all filters to defaults">
              <span class="material-symbols-outlined text-[19px] leading-none text-slate-600">rotate_left</span>
              <span>Reset</span>
            </button>

            <!-- Segmented Control -->
            <!-- <div class="segmented-control-wrapper shrink-0">
              <button class="segment" [class.active]="activeView === 'employee'" (click)="setActiveView('employee')">
                <span class="material-symbols-outlined text-[15px] mr-1.5 leading-none">person</span>
                By Employee
              </button>
              <button class="segment" [class.active]="activeView === 'project'" (click)="setActiveView('project')">
                <span class="material-symbols-outlined text-[15px] mr-1.5 leading-none">folder</span>
                By Project
              </button>
            </div> -->
          </div>
        </div>

        <!-- View 1: By Employee Tabular View -->
        <div *ngIf="activeView === 'employee'">
          <!-- Loading Spinner -->
          <div *ngIf="loading" class="py-16 flex flex-col items-center justify-center text-slate-500">
            <mat-spinner diameter="36" class="mb-3"></mat-spinner>
            <span class="text-sm font-medium text-slate-600">Retrieving Conduct employee timelogs...</span>
          </div>

          <!-- Empty State -->
          <div *ngIf="!loading && (!groupedEmployeeTimelogs || groupedEmployeeTimelogs.length === 0)"
            class="py-16 px-4 text-center">
            <span class="material-symbols-outlined text-5xl text-slate-300 mb-2">person_off</span>
            <h3 class="text-base font-semibold text-slate-700 m-0">No employee timelogs found</h3>
            <p class="text-xs sm:text-sm text-slate-400 mt-1 max-w-md mx-auto">
              No Conduct timelogs match your current filters or date. Try selecting another date or clearing your
              project filter.
            </p>
            <button *ngIf="selectedProjectId !== 'all' || searchQuery" mat-stroked-button
              (click)="clearProjectFilter(); searchQuery = ''; onSearchChange()"
              class="mt-4 !text-xs font-semibold !text-[#5459d1] !border-[#5459d1]">
              Reset Filters
            </button>
          </div>

          <!-- Table Section Header -->
          <div *ngIf="!loading && groupedEmployeeTimelogs && groupedEmployeeTimelogs.length > 0"
            class="flex items-center justify-between flex-wrap gap-2 mb-3.5">
            <div>
              <h3 class="text-sm sm:text-base font-bold text-gray-900 m-0 tracking-tight">Employee timelogs</h3>
              <p class="text-xs text-gray-400 m-0 mt-0.5 font-normal">
                {{ groupedEmployeeTimelogs.length }} employees · {{ totalLogsCount }} entries · sorted by unlogged time,
                high to low
              </p>
            </div>
          </div>

          <!-- Clean Table adhering to SuperSee styling -->
          <div *ngIf="!loading && groupedEmployeeTimelogs && groupedEmployeeTimelogs.length > 0"
            class="overflow-x-auto">
            <table class="w-full text-left border-collapse text-sm">
              <thead>
                <tr class="bg-gray-50/80 border-b border-gray-200/90 text-gray-500 font-semibold text-sm capitalize">
                  <th class="py-3 px-4 min-w-[200px]">Employee</th>
                  <th class="py-3 px-4 min-w-[170px]">Projects</th>
                  <th class="py-3 px-4 min-w-[220px]">Latest Task</th>
                  <th class="py-3 px-4 min-w-[140px]">Logged on Conduct</th>
                  <th class="py-3 px-4 min-w-[140px]">Tracked by SuperSee</th>
                  <th class="py-3 px-4 min-w-[130px]">Unlogged</th>
                  <th class="py-3 px-4 text-right min-w-[130px]">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr *ngFor="let group of pagedEmployeeTimelogs; let i = index"
                  (click)="openDrawer(group, (pageIndex * pageSize) + i)"
                  class="cursor-pointer bg-white hover:bg-slate-50/80 transition-colors group">
                  <!-- 1. Employee -->
                  <td class="py-3.5 px-4 font-semibold text-gray-900">
                    <div class="flex items-center gap-3">
                      <ngx-avatars [name]="group.employee_name || 'User'" size="36" [initialsSize]="2"
                        [textSizeRatio]="3" class="rounded-full shrink-0">
                      </ngx-avatars>
                      <div class="flex flex-col min-w-0">
                        <span
                          class="font-bold text-gray-900 text-sm truncate group-hover:text-[#5459d1] transition-colors">
                          {{ group.employee_name }}
                        </span>
                        <span class="text-xs text-gray-500 font-normal truncate" *ngIf="group.employee_email">
                          {{ group.employee_email }}
                        </span>
                      </div>
                    </div>
                  </td>

                  <!-- 2. Projects -->
                  <td class="py-3.5 px-4">
                    <div class="flex items-center gap-1.5 flex-wrap max-w-[220px]">
                      <span *ngIf="group.projects && group.projects.length > 0"
                        [class]="'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-semibold border ' + (group.projects[0].palette?.tagBg || 'bg-indigo-50') + ' ' + (group.projects[0].palette?.tagText || 'text-indigo-700') + ' ' + (group.projects[0].palette?.tagBorder || 'border-indigo-200')"
                        [matTooltip]="group.all_projects_label || group.projects[0].name">
                        <span class="material-symbols-outlined text-[13px] leading-none">folder</span>
                        <span class="truncate max-w-[120px]">{{ group.projects[0].name }}</span>
                      </span>
                      <span *ngIf="group.other_projects_count && group.other_projects_count > 0"
                        class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 cursor-pointer"
                        [matTooltip]="group.all_projects_label || ''">
                        +{{ group.other_projects_count }}
                      </span>
                      <span *ngIf="!group.projects || group.projects.length === 0" class="text-xs text-gray-400 italic">
                        General Tasks
                      </span>
                    </div>
                  </td>

                  <!-- 3. Latest Task -->
                  <td class="py-3.5 px-4">
                    <div class="flex items-center gap-1.5 text-sm text-slate-700 max-w-[260px]"
                      [matTooltip]="group.latest_task_title || 'General Task'">
                      <span
                        class="material-symbols-outlined text-slate-400 text-[15px] shrink-0 leading-none">task_alt</span>
                      <span class="truncate font-medium text-slate-800">
                        {{ group.latest_task_title || 'General Task' }}
                      </span>
                    </div>
                  </td>

                  <!-- 4. Conduct Logged Time -->
                  <td class="py-3.5 px-4">
                    <div class="flex flex-col items-start gap-0.5">
                      <span class="conduct-table-time font-bold text-gray-900 text-xs sm:text-sm">
                        {{ group.total_worked_hours }}
                      </span>
                      <span class="text-[11px] text-gray-400 font-medium ml-0.5">
                        {{ group.logs.length }} {{ group.logs.length === 1 ? 'Task' : 'Tasks' }} Logged
                      </span>
                    </div>
                  </td>

                  <!-- 5. SuperSee Tracked Time -->
                  <td class="py-3.5 px-4">
                    <span class="conduct-table-time font-semibold text-slate-700 text-xs sm:text-sm">
                      {{ group.supersee_tracked_hours || '--' }}
                    </span>
                  </td>

                  <!-- 6. Unlogged Time -->
                  <td class="py-3.5 px-4">
                    <span class="conduct-table-time font-bold text-xs sm:text-sm"
                      [ngClass]="(group.unlogged_seconds && group.unlogged_seconds > 0) ? 'text-rose-600' : 'text-slate-500'">
                      {{ group.unlogged_hours || '00h 00m' }}
                    </span>
                  </td>

                  <!-- 7. Actions -->
                  <td class="py-3.5 px-4 text-right">
                    <button color="primary"
                      (click)="openDrawer(group, (pageIndex * pageSize) + i); $event.stopPropagation()"
                      class="!h-8 !px-3 !text-xs !font-semibold !rounded-md inline-flex items-center gap-1.5 border border-solid !border-[#5459d1] !text-[#5459d1] bg-white hover:!bg-[#5459d1] hover:!text-white hover:shadow-2xs transition-all duration-150 cursor-pointer">
                      <span>View Details</span>
                      <mat-icon class="!text-sm !w-4 !h-4 leading-none">arrow_forward</mat-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mat Paginator matching Employee List Page & SuperSee Theme -->
          <mat-paginator *ngIf="!loading && groupedEmployeeTimelogs && groupedEmployeeTimelogs.length > 0"
            [length]="groupedEmployeeTimelogs.length" [pageSize]="pageSize" [pageSizeOptions]="pageSizeOptions"
            [pageIndex]="pageIndex" aria-label="Select page" (page)="onPaginateChange($event)" #EmpPaginator
            class="conduct-paginator">
          </mat-paginator>
        </div>

        <!-- View 2: By Project Grid View -->
        <div *ngIf="activeView === 'project'">
          <!-- Loading Spinner -->
          <div *ngIf="loading" class="py-16 flex flex-col items-center justify-center text-slate-500">
            <mat-spinner diameter="36" class="mb-3"></mat-spinner>
            <span class="text-sm font-medium text-slate-600">Retrieving project summaries...</span>
          </div>

          <!-- Empty State -->
          <div *ngIf="!loading && (!projectSummaries || projectSummaries.length === 0)" class="py-16 px-4 text-center">
            <span class="material-symbols-outlined text-5xl text-slate-300 mb-2">folder_off</span>
            <h3 class="text-base font-semibold text-slate-700 m-0">No projects found</h3>
            <p class="text-xs sm:text-sm text-slate-400 mt-1 max-w-md mx-auto">
              No Conduct tasks have been logged for this date or matching your search filter.
            </p>
          </div>

          <!-- Project Cards Grid -->
          <div *ngIf="!loading && projectSummaries && projectSummaries.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div *ngFor="let proj of projectSummaries"
              class="conduct-project-card group relative bg-white rounded-xl border border-solid border-gray-200 hover:border-[#5459d1]/40 p-4 shadow-2xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between overflow-hidden">
              <!-- Top Accent Line -->
              <div class="absolute top-0 left-0 right-0 h-1" [style.backgroundColor]="proj.colorPalette.barHex"></div>

              <div>
                <!-- Top Row: Project Icon + Title & Tasks Pill -->
                <div class="flex items-center justify-between gap-2 mb-3.5 pt-1">
                  <div class="flex items-center gap-2 min-w-0">
                    <span
                      class="inline-flex items-center justify-center w-7 h-7 rounded-lg border shrink-0"
                      [ngClass]="[proj.colorPalette.tagBg, proj.colorPalette.tagText, proj.colorPalette.tagBorder]">
                      <mat-icon class="!text-sm !w-4 !h-4 leading-none">folder</mat-icon>
                    </span>
                    <span class="text-sm font-semibold text-slate-800 truncate" [matTooltip]="proj.project_name">
                      {{ proj.project_name }}
                    </span>
                  </div>
                  <span class="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full shrink-0">
                    {{ proj.total_tasks_count }} {{ proj.total_tasks_count === 1 ? 'task' : 'tasks' }}
                  </span>
                </div>

                <!-- Middle Hero: Logged Hours + Clock Visual Indicator -->
                <div class="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <div class="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Time Logged</div>
                    <div class="conduct-project-card-time text-2xl font-bold text-slate-800 tracking-tight flex items-baseline gap-1.5">
                      <span>{{ proj.total_worked_hours }}</span>
                    </div>
                  </div>
                  <div
                    class="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 group-hover:text-[#5459d1] group-hover:bg-[#5459d1]/5 group-hover:border-[#5459d1]/20 transition-all duration-200 shrink-0">
                    <mat-icon class="!text-xl !w-5 !h-5 leading-none">schedule</mat-icon>
                  </div>
                </div>
              </div>

              <!-- Footer: Contributor Avatars & Direct Filter Action -->
              <div class="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 mt-2">
                <div class="flex items-center gap-2 min-w-0">
                  <!-- 1 Contributor: Avatar + Name -->
                  <div *ngIf="proj.contributors && proj.contributors.length === 1" class="flex items-center gap-2 min-w-0">
                    <ngx-avatars [name]="proj.contributors[0].name || 'User'" size="26" [initialsSize]="2" [textSizeRatio]="3"
                      class="rounded-full ring-2 ring-white inline-block shadow-2xs shrink-0" [matTooltip]="proj.contributors[0].name">
                    </ngx-avatars>
                    <span class="text-xs font-medium text-slate-600 truncate max-w-[110px]" [matTooltip]="proj.contributors[0].name">
                      {{ proj.contributors[0].name }}
                    </span>
                  </div>

                  <!-- Multiple Contributors: Stacked Avatars -->
                  <div *ngIf="proj.contributors && proj.contributors.length > 1" class="flex items-center -space-x-2 overflow-hidden py-0.5">
                    <ng-container *ngFor="let c of proj.contributors.slice(0, 3)">
                      <ngx-avatars [name]="c.name || 'User'" size="26" [initialsSize]="2" [textSizeRatio]="3"
                        class="rounded-full ring-2 ring-white inline-block shadow-2xs" [matTooltip]="c.name">
                      </ngx-avatars>
                    </ng-container>
                    <span *ngIf="proj.contributors.length > 3"
                      class="inline-flex items-center justify-center w-6.5 h-6.5 text-[10px] font-bold text-slate-600 bg-slate-100 rounded-full ring-2 ring-white shrink-0">
                      +{{ proj.contributors.length - 3 }}
                    </span>
                  </div>

                  <!-- Fallback if no contributors -->
                  <div *ngIf="!proj.contributors || proj.contributors.length === 0" class="flex items-center gap-1 text-xs text-slate-400">
                    <mat-icon class="!text-sm !w-4 !h-4 leading-none text-slate-400">person_outline</mat-icon>
                    <span>Conduct</span>
                  </div>
                </div>

                <button type="button" (click)="setActiveView('employee'); onProjectFilterChange(proj.project_id)"
                  class="text-xs font-semibold text-[#5459d1] hover:text-[#4348b8] inline-flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 shrink-0">
                  <span>Filter Employees</span>
                  <mat-icon class="!text-sm !w-4 !h-4 leading-none">arrow_forward</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Conduct Timelogs Detail Drawer now hosted via centralized App Shared Drawer -->
</div>